import { ESCALATED_TO, ROLE_ID_TO_ESCALATION_TEAM, TASK_DISPLAY_STATUS } from './constants';
import { getTaskDisplayStatus } from './helpers';

export const STAFF_CAPACITY = 8;

const TASK_TEAM_TO_ESCALATED_TO = {
  member: ESCALATED_TO.MemberExperience,
  ops: ESCALATED_TO.Operations,
  clinical: ESCALATED_TO.Clinical,
  rcm: ESCALATED_TO.RCM,
};

const PRIORITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 };

export function getStaffDisplayName(client) {
  if (typeof client?.getName === 'function') return client.getName(false, true);
  return `${client?.firstName || ''} ${client?.lastName || ''}`.trim();
}

export function getStaffInitials(client) {
  const name = getStaffDisplayName(client);
  const parts = name
    .replace(/^Dr\.\s+/i, 'Dr ')
    .trim()
    .split(/\s+/);
  if (!parts.length || !parts[0]) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function getStaffRole(client) {
  return client?.role || '';
}

function getClientEscalationTeams(client) {
  const teams = ROLE_ID_TO_ESCALATION_TEAM[client.roleId];
  if (teams == null) return [];
  return Array.isArray(teams) ? teams : [teams];
}

function priorityRank(task) {
  return PRIORITY_RANK[task.priority || 'low'] ?? PRIORITY_RANK.low;
}

export function isEligible(client, task) {
  const requiredTeam = TASK_TEAM_TO_ESCALATED_TO[task.team];
  if (requiredTeam == null) return false;
  return getClientEscalationTeams(client).includes(requiredTeam);
}

export function eligibleStaff(task, clients, activeTeam = 'all') {
  return clients.filter((client) => {
    if (!isEligible(client, task)) return false;
    if (activeTeam === 'all') return true;
    const scopeTeam = TASK_TEAM_TO_ESCALATED_TO[activeTeam];
    if (scopeTeam == null) return true;
    return getClientEscalationTeams(client).includes(scopeTeam);
  });
}

export function staffForTeam(clients, activeTeam = 'all') {
  if (activeTeam === 'all') return clients;
  const scopeTeam = TASK_TEAM_TO_ESCALATED_TO[activeTeam];
  if (scopeTeam == null) return clients;
  return clients.filter((client) => getClientEscalationTeams(client).includes(scopeTeam));
}

export function computeLoadByClientId(allTasks, clients) {
  const clientIdByName = new Map();
  clients.forEach((client) => {
    const name = getStaffDisplayName(client).toLowerCase();
    if (name) clientIdByName.set(name, client.id);
  });

  const load = {};
  allTasks.forEach((task) => {
    const owner = `${task.owner || ''}`.trim().toLowerCase();
    if (!owner || owner === 'unassigned') return;
    if (getTaskDisplayStatus(task) === TASK_DISPLAY_STATUS.Done) return;
    const clientId = clientIdByName.get(owner);
    if (clientId == null) return;
    load[clientId] = (load[clientId] || 0) + 1;
  });

  return load;
}

export function balanceAssign(selectedTasks, clients, loadByClientId, activeTeam = 'all') {
  const load = new Map();
  clients.forEach((client) => load.set(client.id, loadByClientId[client.id] ?? 0));

  const ordered = [...selectedTasks].sort((a, b) => {
    const pr = priorityRank(a) - priorityRank(b);
    if (pr !== 0) return pr;
    const da = a.due ?? '';
    const db = b.due ?? '';
    if (da !== db) return da < db ? -1 : 1;
    return `${a.id}` < `${b.id}` ? -1 : `${a.id}` > `${b.id}` ? 1 : 0;
  });

  const proposals = [];
  const unassignableTaskIds = [];
  let rrCursor = 0;

  ordered.forEach((task) => {
    const eligible = eligibleStaff(task, clients, activeTeam);
    if (!eligible.length) {
      unassignableTaskIds.push(`${task.id}`);
      return;
    }

    const minLoad = Math.min(...eligible.map((c) => load.get(c.id) ?? 0));
    const tied = eligible
      .filter((c) => (load.get(c.id) ?? 0) === minLoad)
      .sort((a, b) => a.id - b.id);

    const chosen = tied[rrCursor % tied.length];
    rrCursor++;

    const currentOwner = `${task.owner || ''}`.trim();
    const chosenName = getStaffDisplayName(chosen);
    if (chosenName.toLowerCase() === currentOwner.toLowerCase()) return;

    proposals.push({
      taskId: `${task.id}`,
      fromOwner: currentOwner || null,
      toClientId: chosen.id,
      toDisplayName: chosenName,
    });
    load.set(chosen.id, (load.get(chosen.id) ?? 0) + 1);
  });

  return { proposals, unassignableTaskIds };
}
